import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { format } from 'date-fns';
import { dashboardAPI } from '../utils/api';
import { 
  FiDollarSign, FiUsers, FiPackage, FiFileText, 
  FiCreditCard, FiClock, FiCheckCircle, FiAlertTriangle 
} from 'react-icons/fi';
import './Dashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, revenueRes, recentRes, overdueRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getRevenue(new Date().getFullYear()),
        dashboardAPI.getRecentInvoices(5),
        dashboardAPI.getOverdue(5),
      ]);

      setStats(statsRes.data.stats);
      setRevenueData(revenueRes.data.revenue);
      setRecentInvoices(recentRes.data.invoices);
      setOverdueInvoices(overdueRes.data.invoices);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'AED') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return format(new Date(date), 'MMM dd, yyyy');
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'badge-draft',
      sent: 'badge-sent',
      viewed: 'badge-viewed',
      paid: 'badge-paid',
      partial: 'badge-partial',
      overdue: 'badge-overdue',
      cancelled: 'badge-cancelled',
    };
    return statusClasses[status] || 'badge-draft';
  };

  const chartData = {
    labels: revenueData.map(d => d.monthName),
    datasets: [
      {
        label: 'Total Amount',
        data: revenueData.map(d => d.totalAmount),
        backgroundColor: 'rgba(79, 70, 229, 0.08)',
        borderColor: '#4f46e5',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Paid Amount',
        data: revenueData.map(d => d.paidAmount),
        backgroundColor: 'rgba(5, 150, 105, 0.08)',
        borderColor: '#059669',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => formatCurrency(value),
        },
      },
    },
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Welcome back! Here's what's happening with your invoices.</p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-icon primary">
              <FiDollarSign />
            </div>
          </div>
          <div className="stat-card-value">
            {formatCurrency(stats?.total_revenue || 0)}
          </div>
          <div className="stat-card-label">Total Revenue</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-icon success">
              <FiCheckCircle />
            </div>
          </div>
          <div className="stat-card-value">
            {formatCurrency(stats?.paid_revenue || 0)}
          </div>
          <div className="stat-card-label">Paid Revenue</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-icon warning">
              <FiClock />
            </div>
          </div>
          <div className="stat-card-value">
            {formatCurrency(stats?.outstanding_revenue || 0)}
          </div>
          <div className="stat-card-label">Outstanding</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-icon danger">
              <FiAlertTriangle />
            </div>
          </div>
          <div className="stat-card-value">
            {formatCurrency(stats?.overdue_amount || 0)}
          </div>
          <div className="stat-card-label">Overdue Amount</div>
        </div>
      </div>

      <div className="dashboard-grid">
        {/* Revenue Chart */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Monthly Revenue</h3>
          </div>
          <div className="card-body">
            <div className="chart-container">
              <Line data={chartData} options={chartOptions} height={300} />
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Quick Stats</h3>
          </div>
          <div className="card-body">
            <div className="quick-stats">
              <div className="quick-stat-item">
                <FiFileText className="quick-stat-icon" />
                <div className="quick-stat-info">
                  <span className="quick-stat-value">{stats?.total_invoices || 0}</span>
                  <span className="quick-stat-label">Total Invoices</span>
                </div>
              </div>
              <div className="quick-stat-item">
                <FiUsers className="quick-stat-icon" />
                <div className="quick-stat-info">
                  <span className="quick-stat-value">{stats?.total_clients || 0}</span>
                  <span className="quick-stat-label">Total Clients</span>
                </div>
              </div>
              <div className="quick-stat-item">
                <FiPackage className="quick-stat-icon" />
                <div className="quick-stat-info">
                  <span className="quick-stat-value">{stats?.total_items || 0}</span>
                  <span className="quick-stat-label">Total Items</span>
                </div>
              </div>
              <div className="quick-stat-item">
                <FiCreditCard className="quick-stat-icon" />
                <div className="quick-stat-info">
                  <span className="quick-stat-value">{formatCurrency(stats?.total_collected || 0)}</span>
                  <span className="quick-stat-label">Collected This Month</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Invoices */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recent Invoices</h3>
            <Link to="/invoices" className="btn btn-sm btn-secondary">
              View All
            </Link>
          </div>
          <div className="card-body">
            {recentInvoices.length === 0 ? (
              <div className="empty-state">
                <p>No invoices yet</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Client</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentInvoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>
                          <Link to={`/invoices/${invoice.id}`} className="text-primary font-medium">
                            {invoice.invoice_number}
                          </Link>
                        </td>
                        <td>{invoice.company_name}</td>
                        <td>{formatCurrency(invoice.total, invoice.currency)}</td>
                        <td>
                          <span className={`badge ${getStatusBadge(invoice.status)}`}>
                            {invoice.status}
                          </span>
                        </td>
                        <td>{formatDate(invoice.issue_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Overdue Invoices */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              <FiAlertTriangle className="text-danger" /> Overdue Invoices
            </h3>
            <Link to="/invoices?status=overdue" className="btn btn-sm btn-secondary">
              View All
            </Link>
          </div>
          <div className="card-body">
            {overdueInvoices.length === 0 ? (
              <div className="empty-state">
                <FiCheckCircle className="empty-state-icon text-success" />
                <p className="empty-state-title">All caught up!</p>
                <p className="empty-state-text">No overdue invoices in the System</p>
              </div>
            ) : (
              <div className="table-container overdue-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Client</th>
                      <th>Due Date</th>
                      <th className="days-col">Days</th>
                      <th className="amount-col">Amount Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overdueInvoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>
                          <Link to={`/invoices/${invoice.id}`} className="text-primary font-medium">
                            {invoice.invoice_number}
                          </Link>
                        </td>
                        <td className="client-col" title={invoice.company_name}>{invoice.company_name}</td>
                        <td>{formatDate(invoice.due_date)}</td>
                        <td className="text-danger font-medium">{invoice.days_overdue}</td>
                        <td className="text-danger font-medium nowrap">
                          {formatCurrency(invoice.amount_due, invoice.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;